// lib/axios.ts
import axios, { type AxiosResponse, type InternalAxiosRequestConfig, type AxiosError } from 'axios';
import { NO_AUTH_REQUIRED_API_ROUTES, API_ROUTES, LOGOUT_ERROR_CODES } from '@/constants';
import { useAuthStore } from '@/stores/useAuthStore';

// ============================================
// Instance
// ============================================

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/',
  // withCredentials: true,
  timeout: 120_000, // 2 minutes — generous for AI generation endpoints
});

// ============================================
// Error extraction
// ============================================

/**
 * Extract a human-readable error message from any caught error.
 *
 * Priority:
 *  1. Axios response body `{ error: "..." }` or `{ detail: "..." }`
 *  2. Axios response body `{ errors: [...] }` (joined)
 *  3. `Error.message`
 *  4. Fallback string
 */
export function extractApiError(err: unknown, fallback = 'Unknown error.'): string {
  if (axios.isAxiosError(err)) {
    const data = (err as AxiosError<Record<string, unknown>>).response?.data;
    if (data) {
      if (typeof data.error === 'string') return data.error;
      if (typeof data.detail === 'string') return data.detail;
      if (Array.isArray(data.errors)) return data.errors.join('. ');
    }
    if (err.code === 'ECONNABORTED') return 'Request timed out. Please try again.';
    if (!err.response) return 'No server connection.';
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

// ============================================
// Request Interceptor
// ============================================

api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    if (NO_AUTH_REQUIRED_API_ROUTES.includes(config.url || '')) {
      return config;
    }

    const token = await useAuthStore.getState().getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// ============================================
// Response Interceptor
// ============================================

api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error: AxiosError) => {
    const { response, config: originalRequest } = error;

    if (!response || !originalRequest) {
      return Promise.reject(error);
    }

    const { status } = response;
    const { logOut, getAccessToken } = useAuthStore.getState();

    // 503 - Maintenance
    if (status === 503) {
      return Promise.reject(error);
    }

    // 401 - Unauthorized
    if (status === 401) {
      // If the refresh endpoint itself failed, just reject — getAccessToken handles logout
      if (originalRequest.url === API_ROUTES.refresh) {
        return Promise.reject(error);
      }

      const errorCode = (response.data as { code?: string })?.code || '';

      if (LOGOUT_ERROR_CODES.includes(errorCode)) {
        logOut();
        return Promise.reject(error);
      }

      const shouldAttemptRefresh = !NO_AUTH_REQUIRED_API_ROUTES.includes(originalRequest.url || '');

      if (shouldAttemptRefresh) {
        try {
          const newToken = await getAccessToken();
          if (newToken) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return api(originalRequest);
          }
        } catch {
          logOut();
        }
      }
    }

    return Promise.reject(error);
  },
);
